import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import GameCard from '../GameCard';

const animationDuration = 200;

const Container = styled.section`
  position: relative;
  z-index: 1;
`;

const SlideTrack = styled.div(({ visibleSlideCount }) => {
  const slideWidth = 100 / visibleSlideCount;

  return `
    width: 100%;
    overflow-x: hidden;
    position: relative;
    white-space: nowrap;
    z-index: 1;

    & > div {
      display: inline-block;
      width: calc(${slideWidth}% + ${16 / visibleSlideCount}px);
      display: inline-block;
      padding: 0 0 0 16px;
      transform: translateX(-100%);

      &:first-child {
        margin: 0 0 0 -16px;
      }
    }

    &.animate > div {
      @media (prefers-reduced-motion: no-preference) {
        transition: transform ease-in-out ${animationDuration}ms;
      }
    }

    &.prev > div {
      transform: translateX(0%);
    }

    &.next > div {
      transform: translateX(-200%);
    }
  `;
});

const NavButton = styled.button(({ theme }) => {
  const { color } = theme;

  return `
    height: 32px;
    width: 32px;
    background-color: ${color.grayscale[1000]};
    border-radius: 50%;
    color: ${color.primary.regular};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

    &:hover {
      color: ${color.grayscale[1000]};
      background-color: ${color.primary.regular};
    }

    & > svg {
      width: 9px;
    }
  `;
});

const HiddenHeading = styled.h3`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

const LiveRegion = styled.div`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

const PrevButton = styled(NavButton)`
  left: -16px;
`;

const NextButton = styled(NavButton)`
  right: -16px;
`;

const getVisibleSlideCount = () => {
  const visibleSlidesLarge = 3;
  const visibleSlidesSmall = 2;

  if (window.innerWidth >= 1280) {
    return visibleSlidesLarge;
  }

  if (window.innerWidth < 1280) {
    return visibleSlidesSmall;
  }
};

const getSlideIndexes = (slides, visibleSlideCount, indexSlide) => {
  const totalSlides = slides.length;
  const slideArray = [];

  for (let i = -1; i < visibleSlideCount + 1; i++) {
    if (i + indexSlide < 0) {
      slideArray.push(totalSlides + i);
    } else if (i + indexSlide < totalSlides) {
      slideArray.push(i + indexSlide);
    } else {
      slideArray.push(i + indexSlide - totalSlides);
    }
  }

  return slideArray;
};

const Carousel = (props) => {
  const { handleRoomSelect, slides } = props;
  const [indexSlide, setIndexSlide] = useState(1);
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const [movementDirection, setMovementDirection] = useState(false);
  const [visibleSlideCount, setVisibleSlideCount] = useState(getVisibleSlideCount());
  const [slideIndexes, setSlideIndexes] = useState([]);
  const [liveRegionText, setLiveRegionText] = useState();
  const totalSlides = slides.length;
  const initialSlideLoad = useRef(true);
  const carouselRef = useRef();
  const timeRef = useRef(
    slides.map((slide) => {
      return slide.time + Math.round(Math.random() * 15);
    })
  );

  const handleSlideNav = (direction) => {
    if (!animationEnabled && direction === 'prev') {
      setAnimationEnabled(true);
      setMovementDirection('prev');

      setTimeout(() => {
        if (indexSlide > 0) {
          setIndexSlide(indexSlide - 1);
        } else {
          setIndexSlide(totalSlides - 1);
        }

        setAnimationEnabled(false);
        setMovementDirection(false);
      }, animationDuration);
    }

    if (!animationEnabled && direction === 'next') {
      setAnimationEnabled(true);
      setMovementDirection('next');

      setTimeout(() => {
        if (indexSlide < totalSlides - 1) {
          setIndexSlide(indexSlide + 1);
        } else {
          setIndexSlide(0);
        }

        setAnimationEnabled(false);
        setMovementDirection(false);
      }, animationDuration);
    }

    initialSlideLoad.current = false;
  };

  const handleResize = () => {
    const showSlides = getVisibleSlideCount();

    if (visibleSlideCount !== showSlides) {
      setVisibleSlideCount(showSlides);
    }
  };

  const handleKeyup = (e) => {
    if (e.keyCode === 37 || e.keyCode === 39) {
      const direction = e.keyCode === 37 ? 'prev' : 'next';
      handleSlideNav(direction);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    const effectRef = carouselRef.current;
    effectRef.addEventListener('keyup', handleKeyup);

    const timer = setInterval(() => {
      timeRef.current = timeRef.current.map((time, index) => {
        return time - 1 >= 0 ? time - 1 : slides[index].timer;
      });

      const tickEvent = new Event('carouselTick');

      window.dispatchEvent(tickEvent);
    }, 1000);

    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', handleResize);
      effectRef.removeEventListener('keyup', handleKeyup);
    };
  });

  useEffect(() => {
    setSlideIndexes(getSlideIndexes(slides, visibleSlideCount, indexSlide));
  }, [indexSlide, slides, visibleSlideCount]);

  useLayoutEffect(() => {
    if (!initialSlideLoad.current) {
      let visibleSlides = '';
      const iterations = slideIndexes.length;

      for (let i = 1; i < iterations - 1; i++) {
        visibleSlides += slides[slideIndexes[i]].title;
        visibleSlides += i !== iterations - 2 ? ', ' : ' ';
      }

      setLiveRegionText(`Showing games: ${visibleSlides}`);
    }
  }, [slides, slideIndexes]);

  const slideLoop = slideIndexes.map((slideIndex, index) => {
    const slide = slides[slideIndex];
    const isVisible = index === 0 || index === slideIndexes.length - 1 ? false : true;
    return (
      <div key={`carouselSlide${slideIndex}${index}`}>
        <GameCard
          title={slide.name}
          getTimeRef={() => timeRef.current[slideIndex]}
          description=""
          gameName={slide.game_name}
          info={slide.info}
          isVisible={isVisible}
          callback={() => {
            handleRoomSelect(slide.game_name, slide.key);
          }}
          multiplayer
        />
      </div>
    );
  });

  useEffect(() => {
    window.carouselTimer = 0;
  });

  return (
    <Container ref={carouselRef} aria-labelledby="liveGameCarouselHeader">
      <HiddenHeading id="liveGameCarouselHeader">Current Live Games</HiddenHeading>

      <PrevButton type="button" onClick={() => handleSlideNav('prev')} aria-label="Previous games">
        <FontAwesomeIcon icon={faChevronLeft} />
      </PrevButton>

      <SlideTrack
        visibleSlideCount={visibleSlideCount}
        className={`${animationEnabled && 'animate'} ${movementDirection && movementDirection}`}
      >
        {slideLoop}
      </SlideTrack>

      <NextButton type="button" onClick={() => handleSlideNav('next')} aria-label="Next games">
        <FontAwesomeIcon icon={faChevronRight} />
      </NextButton>

      <LiveRegion aria-atomic aria-live="polite">
        {liveRegionText}
      </LiveRegion>
    </Container>
  );
};

export default Carousel;
